import { VerificationSchema, VerificationValues } from '@/forms/Register'
import {
  Verification,
  ResendVerification,
  ResendVerificationEmail,
  ResendWhatsappVerification
} from '@/services/api/auth'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import toast from 'react-hot-toast'
import CustomToast from '../CustomToast'
import { useMutation } from 'react-query'
import SubmitButton from '../forms/SubmitButton'
import TextInput from '../forms/TextInput'
import Illustration from './Illustration'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import OTPInput from '../forms/OTPInput'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from '@/components/forms/PhoneInput'

type InternalView = 'verify' | 'editPhone' | 'editEmail'

interface Props {
  fromRegister?: boolean
  setIsOpen: (arg0: boolean) => void
  setVerificationData: any
  setShouldVerify: any
  phone: { country_code: string; number: string }
  email: string
  postLoginCallBack?: any
  setPortLoginCallBack?: any
  productType?: any
  setProductType?: any
  executePostLoginCallback: () => void
  setCurrentView?: any
  customSubmitHandler?: (code: string) => void;
  onBack?: () => void;
  isVerifying?: boolean;
  // New callbacks for updating contact info
  onPhoneChange?: (phone: { country_code: string; number: string }) => void;
  onEmailChange?: (email: string) => void;
  showButton?: boolean
  setShowButton?: any
}

export default function Verifications({
  phone,
  fromRegister = false,
  setIsOpen,
  setShouldVerify,
  setVerificationData,
  postLoginCallBack,
  setPortLoginCallBack,
  productType,
  setProductType,
  executePostLoginCallback,
  setCurrentView,
  email,
  customSubmitHandler,
  onBack,
  isVerifying,
  onPhoneChange,
  onEmailChange,
  showButton,
  setShowButton
}: Props) {
  const [internalView, setInternalView] = React.useState<InternalView>('verify')
  const [editedPhone, setEditedPhone] = React.useState(phone)
  const [editedEmail, setEditedEmail] = React.useState(email)
  const [phoneError, setPhoneError] = React.useState<string | undefined>()
  const [emailError, setEmailError] = React.useState<string | undefined>()
  const [isActive, setIsActive] = React.useState(false)
  const [seconds, setSeconds] = React.useState(60)
  const {locale} = useRouter();
  const userLocale = locale === 'ar' ? 'ar' : 'en'
  const [currentMethod, setCurrentMethod] = React.useState<'whatsapp' | 'phone' | 'email'>('whatsapp')
  const { t: tCommon } = useTranslation('common')
  const { t: tAlerts } = useTranslation('alerts')

  function toggle() {
    setIsActive(!isActive)
  }

  React.useEffect(() => {
    if (seconds === 0) return
    const timer = setInterval(() => setSeconds(s => s - 1), 1000)
    return () => clearInterval(timer)
  }, [seconds])

  const [hasSentInitialVerification, setHasSentInitialVerification] = React.useState(false)

  React.useEffect(() => {
    if (fromRegister && !hasSentInitialVerification) {
      handleVerificationEmail()
      setHasSentInitialVerification(true)
    }
  }, [fromRegister])

  const ResendMutation = useMutation(ResendVerification)
  const ResendEmailMutation = useMutation(ResendVerificationEmail)
  const ResendWhatsappMutation = useMutation(ResendWhatsappVerification)
  const VerificationMutation = useMutation(Verification)

  const handleVerification = () => {
    setCurrentMethod('phone')
    ResendMutation.mutate(
      {
        country_code: phone.country_code,
        number: phone.number,
      },
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((tel) => <CustomToast type='success' t={tel} message={tAlerts('validation_code')} />, {
            duration: 4000,
          })
        },
        onError(error: any) {
          if(error.response.data.message != '')
          {
            toast.custom((t) => <CustomToast type='error' t={t} message={error.response.data.message} />, {
              duration: 4000,
              id: 'Validation Error'
            })
          }
        },
      },
    )
  }

  const handleVerificationEmail = () => {
    setCurrentMethod('email')
    ResendEmailMutation.mutate(
      email,
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((t) => <CustomToast type='success' t={t} message={tAlerts('validation_code')} />, {
            duration: 4000,
          })
        },
        onError(error: any) {
          if(error.response.data.message != '')
          {
            toast.custom((t) => <CustomToast type='error' t={t} message={error.response.data.message} />, {
              duration: 4000,
              id: 'Validation Error'
            })
          }
        },
      },
    )
  }

  const handleVerificationWhatsapp = () => {
    setCurrentMethod('whatsapp')
    setHasSentInitialVerification(true)
    ResendWhatsappMutation.mutate(
      {
        country_code: phone.country_code,
        number: phone.number,
        locale: userLocale
      },
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((t) => <CustomToast type='success' t={t} message={tAlerts('validation_code')} />, {
            duration: 4000,
          })
        },
        onError(error: any) {
          if(error.response.data.message != '')
          {
            toast.custom((t) => <CustomToast type='error' t={t} message={error.response.data.message} />, {
              duration: 4000,
              id: 'Validation Error'
            })
          }
        },
      },
    )
  }

  // Send verification to a new phone (used after editing)
  const sendVerificationToNewPhone = (newPhone: { country_code: string; number: string }) => {
    setCurrentMethod('whatsapp')
    ResendWhatsappMutation.mutate(
      {
        country_code: newPhone.country_code,
        number: newPhone.number,
        locale: userLocale
      },
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((t) => <CustomToast type='success' t={t} message={tAlerts('validation_code')} />, {
            duration: 4000,
          })
        },
        onError(error: any) {
          toast.custom((t) => <CustomToast type='error' t={t} message={error.response.data.message} />, {
            duration: 4000,
            id: 'Validation Error'
          })
        },
      },
    )
  }

  // Send verification to a new email (used after editing)
  const sendVerificationToNewEmail = (newEmail: string) => {
    setCurrentMethod('email')
    ResendEmailMutation.mutate(
      newEmail,
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((t) => <CustomToast type='success' t={t} message={tAlerts('validation_code')} />, {
            duration: 4000,
          })
        },
        onError(error: any) {
          toast.custom((t) => <CustomToast type='error' t={t} message={error.response.data.message} />, {
            duration: 4000,
            id: 'Validation Error'
          })
        },
      },
    )
  }

  const SubmitVerificationPhone = async (
    values: VerificationValues,
    actions: FormikHelpers<VerificationValues>,
  ) => {
    if (customSubmitHandler) {
      customSubmitHandler(values.code);
    } else {
      VerificationMutation.mutate(
        {
          ...phone,
          verification_code: values.code,
          email: editedEmail
        },
        {
          onSuccess() {
            toast.custom((t) => <CustomToast type='success' t={t} message={tAlerts('log_in_proceed')} />, {
              duration: 4000,
            });
            setShouldVerify(false);
            setVerificationData(undefined);
            setCurrentView('login');
            setProductType(null);
          },
          onError(error: any) {
            // toast.custom((t) => <CustomToast type='error' t={t} message={error?.response?.data?.message} />, {
            //   duration: 4000,
            //   id: 'Validation Error'
            // });
          },
        }
      );
    }
  };

  const getHeadingText = () => {
    switch (currentMethod) {
      case 'whatsapp':
        return tCommon('headingWhatsapp')
      case 'phone':
        return tCommon('headingPhone')
      case 'email':
        return tCommon('headingEmail')
      default:
        return tCommon('headingPhone')
    }
  }

  const isSubmitting = customSubmitHandler ? isVerifying : VerificationMutation.isLoading;

  // Handlers for edit views
  const handleConfirmPhoneEdit = () => {
    // Validate phone number
    const fullPhone = `+${editedPhone.country_code}${editedPhone.number}`;
    if (!isValidPhoneNumber(fullPhone)) {
      setPhoneError('Invalid phone number');
      return;
    }
    setPhoneError(undefined);
    // Update parent
    if (onPhoneChange) {
      onPhoneChange(editedPhone);
    }
    // Send verification to the new number
    sendVerificationToNewPhone(editedPhone);
    // Return to verify view
    setInternalView('verify');
  };

  const handleConfirmEmailEdit = () => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmail)) {
      setEmailError('Invalid email address');
      return;
    }
    setEmailError(undefined);
    if (onEmailChange) {
      onEmailChange(editedEmail);
    }
    sendVerificationToNewEmail(editedEmail);
    setInternalView('verify');
  };

  // When entering edit view, initialize local state from props
  React.useEffect(() => {
    if (internalView === 'editPhone') {
      setEditedPhone(phone);
      setPhoneError(undefined);
    }
  }, [internalView]);

  React.useEffect(() => {
    if (internalView === 'editEmail') {
      setEditedEmail(email);
      setEmailError(undefined);
    }
  }, [internalView]);

  // Render edit phone view
  if (internalView === 'editPhone') {
    return (
      <div className="w-full max-w-md mx-auto">
        <button
          onClick={() => {
            if (onBack) {
              onBack();
              setShowButton(true);
            } else if (fromRegister) {
              setShouldVerify(false);
              setVerificationData(undefined);
              setShowButton(true);
            } else {
              setIsOpen(false);
              setShowButton(true);
            }
          }}
          className="absolute top-4 left-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
        >
          {/* Close icon SVG */}
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="16" fill="#E9EDF2"/>
            <path d="M20.5 11.5L11.5 20.5" stroke="#667B99" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11.5 11.5L20.5 20.5" stroke="#667B99" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="flex justify-center mb-6">
          <div className="w-32 h-24 flex items-center justify-center">
            <img src="/images/verification.png" alt="verification" />
          </div>
        </div>

        <h2 className="text-center text-xl font-bold mb-6">{tCommon('changePhone')}</h2>

        <div className="mb-4">
          <PhoneInput
            error={phoneError}
            isError={!!phoneError}
            label={tCommon('newPhoneNumber') + '*'}
            values={{ phone: editedPhone.number, country_code: editedPhone.country_code }}
            setFieldValue={(field: string, value: any) => {
              if (field === 'phone') {
                setEditedPhone(prev => ({ ...prev, number: value }));
              } else if (field === 'country_code') {
                setEditedPhone(prev => ({ ...prev, country_code: value }));
              }
            }}
            setFieldError={() => {}}
            setIso2={() => {}}
            fieldPhoneName="phone"
            fieldCountryCodeName="country_code"
            placeholder={`${tCommon('phonePlaceholder')}`}
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleConfirmPhoneEdit}
            disabled={!editedPhone.number || !editedPhone.country_code}
            className="flex-1 py-3 rounded-xl font-semibold bg-primary text-white disabled:bg-gray-200 disabled:text-gray-500"
          >
            {tCommon('confirmChange')}
          </button>
        </div>
      </div>
    );
  }

  // Render edit email view
  if (internalView === 'editEmail') {
    return (
      <div className="w-full max-w-md mx-auto">
        <button
          onClick={() => {
            if (onBack) {
              onBack();
            } else if (fromRegister) {
              setShouldVerify(false);
              setVerificationData(undefined);
            } else {
              setIsOpen(false);
            }
          }}
          className="absolute top-4 left-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="16" fill="#E9EDF2"/>
            <path d="M20.5 11.5L11.5 20.5" stroke="#667B99" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11.5 11.5L20.5 20.5" stroke="#667B99" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="flex justify-center mb-6">
          <div className="w-32 h-24 flex items-center justify-center">
            <img src="/images/verification.png" alt="verification" />
          </div>
        </div>

        <h2 className="text-center text-xl font-bold mb-6">{tCommon('changeEmail')}</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {tCommon('emailLabel')} *
          </label>
          <input
            type="email"
            value={editedEmail}
            onChange={(e) => setEditedEmail(e.target.value)}
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary ${
              emailError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={`${tCommon('emailPlaceholder')}`}
          />
          {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleConfirmEmailEdit}
            disabled={!editedEmail}
            className="flex-1 py-3 rounded-xl font-semibold bg-primary text-white disabled:bg-gray-200 disabled:text-gray-500"
          >
            {tCommon('confirmChange')}
          </button>
        </div>
      </div>
    );
  }

  // Default verification view (unchanged, except for the change buttons which now set internalView)
  return (
    <div className="w-full max-w-md mx-auto">
      {/* Close button - same as before */}
      <button
        onClick={() => {
          if (onBack) {
            onBack();
          } else if (fromRegister) {
            setShouldVerify(false);
            setVerificationData(undefined);
          } else {
            setIsOpen(false);
          }
        }}
        className="absolute top-4 left-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="16" fill="#E9EDF2"/>
          <path d="M20.5 11.5L11.5 20.5" stroke="#667B99" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.5 11.5L20.5 20.5" stroke="#667B99" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div className="flex justify-center mb-6">
        <div className="w-32 h-24 flex items-center justify-center">
          <img src="/images/verification.png" alt="verification" />
        </div>
      </div>

      <h2 className="text-center text-xl font-bold mb-2">
        {getHeadingText()}
      </h2>

      <p className="text-center text-sm text-gray-500 mb-1">
        {tCommon('codeSentTo')}
      </p>

      <p className="text-center font-semibold mb-6" dir="ltr">
        {
          (currentMethod === 'email') ?
          `${editedEmail}` : `+${editedPhone.country_code} ${editedPhone.number}`
        }
      </p>

      <Formik
        initialValues={{ code: '' }}
        validationSchema={VerificationSchema}
        onSubmit={SubmitVerificationPhone}
      >
        {({ errors, touched, values }) => {
          const isOtpComplete = values.code?.length === 4
          return (
            <Form className="w-full">
              <div className="mb-6">
                <OTPInput name="code" length={4} />
                {errors.code && touched.code && (
                  <p className="text-red-500 text-sm text-center mt-2">
                    {errors.code}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isOtpComplete || VerificationMutation.isLoading}
                className={`
                  w-full py-3 rounded-xl font-semibold mb-4 transition
                  ${
                    isOtpComplete
                      ? 'bg-primary text-white cursor-pointer'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                {tCommon('submitCode')}
              </button>

              <div className='w-full flex flex-col items-center justify-center gap-4 p-4 bg-[#E9EDF299] rounded-xl'>
                <div className='w-full flex items-center justify-center gap-2'>
                  <p className="text-[#667B99] text-sm font-normal">{tCommon('secondsHeading')}</p>
                  <p className="text-[#077B69] text-sm font-bold">
                    {`00:${seconds}`}
                  </p>
                </div>
                <p className='text-primary text-sm font-normal' dir={locale === 'ar' ? 'rtl':'ltr'}>{tCommon('methodsHeading')}</p>
                <div className='flex flex-col gap-2 items-center justify-center w-full'>
                  {/* WhatsApp option */}
                  <div
                    className={`p-3 w-full flex items-center justify-center bg-white rounded-lg gap-2 border-[1px] text-sm font-semibold ${
                      seconds === 0 ? 'cursor-pointer text-primary border-primary' : 'cursor-default border-[#DAE0E7] text-gray-500'
                    }`}
                    onClick={e => {
                      if (seconds === 0) {
                        e.preventDefault()
                        handleVerificationWhatsapp()
                      }
                    }}
                  >
                    <span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.6482 9.58891C11.4502 9.48958 10.4762 9.01091 10.2949 8.94424C10.1129 8.87824 9.98088 8.84558 9.84822 9.04424C9.71688 9.24224 9.33688 9.68825 9.22155 9.82025C9.10622 9.95291 8.99022 9.96891 8.79222 9.87024C8.59422 9.77024 7.95555 9.56158 7.19888 8.88691C6.61022 8.36158 6.21222 7.71291 6.09688 7.51425C5.98155 7.31625 6.08488 7.20891 6.18355 7.11024C6.27288 7.02158 6.38222 6.87891 6.48088 6.76358C6.58022 6.64758 6.61288 6.56491 6.67955 6.43225C6.74555 6.30025 6.71288 6.18491 6.66288 6.08558C6.61288 5.98624 6.21688 5.01091 6.05222 4.61424C5.89088 4.22824 5.72755 4.28091 5.60622 4.27424C5.49088 4.26891 5.35888 4.26758 5.22622 4.26758C5.09422 4.26758 4.87955 4.31691 4.69822 4.51558C4.51688 4.71358 4.00488 5.19291 4.00488 6.16825C4.00488 7.14291 4.71488 8.08491 4.81355 8.21758C4.91288 8.34958 6.21088 10.3509 8.19822 11.2089C8.67088 11.4129 9.03955 11.5349 9.32755 11.6256C9.80222 11.7769 10.2342 11.7556 10.5749 11.7042C10.9556 11.6476 11.7469 11.2249 11.9122 10.7622C12.0775 10.2996 12.0775 9.90291 12.0275 9.82025C11.9782 9.73758 11.8469 9.68824 11.6482 9.58891Z" fill="currentColor"/>
                        <path d="M7.99901 1.33398C4.31767 1.33398 1.33301 4.31865 1.33301 8.00065C1.33301 9.26065 1.68301 10.4407 2.29167 11.446L1.34101 14.6673L4.66434 13.7327C5.67391 14.3336 6.82485 14.6559 7.99967 14.6667C11.6817 14.6667 14.6663 11.682 14.6663 7.99998C14.6663 4.31798 11.6817 1.33398 7.99967 1.33398H7.99901ZM7.99901 13.454C7.01684 13.4548 6.05267 13.1904 5.20834 12.6887L5.00834 12.57L2.94034 13.1513L3.49767 11.1153L3.36701 10.906C2.7231 9.88315 2.43722 8.67539 2.5543 7.47243C2.67138 6.26947 3.18477 5.1395 4.01383 4.26002C4.84288 3.38054 5.94061 2.80141 7.13457 2.61359C8.32853 2.42578 9.55105 2.63993 10.6101 3.2224C11.6691 3.80487 12.5046 4.72266 12.9854 5.83157C13.4661 6.94049 13.5648 8.17769 13.2661 9.34881C12.9673 10.5199 12.2879 11.5586 11.3346 12.3017C10.3814 13.0447 9.20831 13.4501 7.99967 13.454H7.99901Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <p>{tCommon('whatsappCode')}</p>
                  </div>
                  {/* SMS option */}
                  <div
                    className={`p-3 w-full flex items-center justify-center bg-white rounded-lg gap-2 border-[1px] text-sm font-semibold ${
                      seconds === 0 ? 'cursor-pointer text-primary border-primary' : 'cursor-default border-[#DAE0E7] text-gray-500'
                    }`}
                    onClick={e => {
                      if (seconds === 0) {
                        e.preventDefault()
                        handleVerification()
                      }
                    }}
                  >
                    <span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M14 10C14 10.3536 13.8595 10.6928 13.6095 10.9428C13.3594 11.1929 13.0203 11.3333 12.6667 11.3333H4.66667L2 14V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V10Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <p>{tCommon('phoneCode')}</p>
                  </div>
                  {/* Email option */}
                  <div
                    className={`p-3 w-full flex items-center justify-center bg-white rounded-lg gap-2 border-[1px] text-sm font-semibold ${
                      seconds === 0 ? 'cursor-pointer text-primary border-primary' : 'cursor-default border-[#DAE0E7] text-gray-500'
                    }`}
                    onClick={e => {
                      if (seconds === 0) {
                        e.preventDefault()
                        handleVerificationEmail()
                      }
                    }}
                  >
                    <span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.333 2.66602H2.66634C1.92996 2.66602 1.33301 3.26297 1.33301 3.99935V11.9993C1.33301 12.7357 1.92996 13.3327 2.66634 13.3327H13.333C14.0694 13.3327 14.6663 12.7357 14.6663 11.9993V3.99935C14.6663 3.26297 14.0694 2.66602 13.333 2.66602Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14.6663 4.66602L8.68634 8.46602C8.48052 8.59497 8.24255 8.66336 7.99967 8.66336C7.7568 8.66336 7.51883 8.59497 7.31301 8.46602L1.33301 4.66602" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <p>{tCommon('emailCode')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                {currentMethod === 'email' ? (
                  <button
                    type="button"
                    onClick={() => setInternalView('editEmail')}
                    className="text-primary font-semibold underline underline-primary underline-offset-1"
                  >
                    {tCommon('changeEmail')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setInternalView('editPhone')}
                    className="text-primary font-semibold underline underline-primary underline-offset-1"
                  >
                    {tCommon('changePhone')}
                  </button>
                )}

                <div className='w-full flex items-center justify-center'>
                  <p className="text-xs text-[#667B99] mt-2 flex items-center justify-center gap-1 font-normal bg-[#E9EDF280] p-2 rounded-md border-[1px] border-gray-500">
                    {tCommon('secureInfo')}
                  </p>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}